import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { LanguageCode, Unit } from '@mira-avant/mtlibs';
import { UnitService } from '../../../../services/unit.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { ZorroModule } from '../../../../modules/zorro.module';
import { LanguageSelectorComponent } from '../../language-selector/language-selector.component';
import { UnitFormComponent } from '../../../forms/standard/unit-form/unit-form.component';
import { EMPTY_UNIT, UnitText, _ } from '@mira-avant/mtlibs';
import { v4 } from 'uuid';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { InProgressService } from '../../../../services/in-progress.service';
import {
	CmsTable,
	Column,
	ENABLE_COPY_COLUMN,
	ENABLE_DELETE_COLUMN,
	ENABLE_EDIT_COLUMN,
	ENABLE_DISPLAY_ORDER_COLUMN,
	ENABLE_UP_DOWN_COLUMN,
	ENABLE_CHECKBOX_COLUMN,
} from '../cms-table/cms-table';
import { TableFormContainer } from '../table-form-container/table-form-container';
import { HeaderColumn } from '../cms-table/header-column';
import { RowColumn } from '../cms-table/row-column';
import { AuthService } from '../../../../services/auth.service';

@Component({
	selector: 'app-unit-list',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		LanguageSelectorComponent,
		UnitFormComponent,
		CmsTable,
		TableFormContainer,
		RowColumn,
		HeaderColumn,
	],
	templateUrl: './unit-list.component.html',
	styleUrl: './unit-list.component.scss',
})
export class UnitListComponent {
	readonly loading = signal<boolean>(true);
	readonly units = signal<Unit[]>([]);
	readonly uuidsInProgress = signal<string[]>([]);
	readonly editLanguage = signal<string>('en');
	readonly editUnit = signal<Unit | undefined>(undefined);
	readonly columns = signal<Column[]>([]);
	readonly isAdmin = signal<boolean>(false);
	readonly isFormShowing = signal<boolean>(false);

	private sub: Subscription | undefined;

	constructor(
		public unitService: UnitService,
		private authService: AuthService,
		public nav: NavigationService,
		private adminService: AdminService,
		public clipboardService: ClipboardService,
		private inProgressService: InProgressService
	) {
		this.sub = this.unitService.units$.subscribe((units: Unit[]) => {
			this.applyUnits(units);

			this.loading.set(false);
		});

		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
			this.updateTableDetails();
		});
	}

	private updateTableDetails(): void {
		const columns: Column[] = [];

		if (this.isAdmin()) {
			columns.push(ENABLE_CHECKBOX_COLUMN);
		}

		columns.push(ENABLE_DISPLAY_ORDER_COLUMN, {
			key: 'unitName',
			name: 'Name',
			sortOrder: null,
			sortFn: (a: Unit, b: Unit) => a.active_text?.name.localeCompare(b.active_text?.name || '') || 0,
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.units().map(row => ({
				text: row.active_text?.name || '',
				value: row.active_text?.name || '',
			})),
			filterFn: (list: string[], item: Unit) => list.some(name => item.active_text?.name.indexOf(name) !== -1),
		});

		if (this.isAdmin()) {
			columns.push(ENABLE_COPY_COLUMN);
			columns.push(ENABLE_UP_DOWN_COLUMN);
			columns.push(ENABLE_EDIT_COLUMN);
			columns.push(ENABLE_DELETE_COLUMN);
		}

		this.columns.set(columns);
	}

	public select($event: unknown): void {
		const unit = $event as Unit;

		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				courseUuid: this.adminService.selectedCourse$.value.uuid,
				unitUuid: unit.uuid,
			},
		]);
	}

	public copy($event: object): void {
		const unit = $event as Unit;
		this.clipboardService.copyUnit(unit);
	}

	public copyBulk($event: object[]): void {
		const units = $event as Unit[];
		this.clipboardService.copyUnits(units);
	}

	public edit($event: unknown): void {
		const unit = $event as Unit;
		this.editUnit.set(this.editUnit()?.uuid === unit.uuid ? undefined : unit);
		this.isFormShowing.set(this.editUnit() !== undefined);
	}

	public onShowForm(): void {
		this.isFormShowing.set(true);
	}

	public onHideForm(): void {
		this.isFormShowing.set(false);
		this.resetEditState();
	}

	private resetEditState(): void {
		this.editUnit.set(undefined);
		this.editLanguage.set(LanguageCode.EN);
	}

	public async onUnitFormSubmit$($event: any): Promise<void> {
		let submitSucceeded = false;
		this.loading.set(true);
		try {
			if (this.editUnit()) {
				await this.updateUnit$($event);
			} else {
				await this.createNewUnit$($event);
			}

			submitSucceeded = true;
		} catch (error) {
			console.error('Error submitting unit form:', error);
		} finally {
			this.loading.set(false);
			this.resetEditState();

			if (submitSucceeded) {
				this.isFormShowing.set(false);
			}
		}
	}

	private applyUnits(units: Unit[]): void {
		this.units.set(units);
		this.updateTableDetails();

		const unitUuids: string[] = units.map(unit => unit.uuid);

		this.inProgressService.unitsInProgress$(unitUuids).then((uuids: string[]) => {
			this.uuidsInProgress.set(uuids);
		});

		const currentEditUnit = this.editUnit();
		if (currentEditUnit) {
			const refreshedEditUnit = units.find(unit => unit.uuid === currentEditUnit.uuid);
			this.editUnit.set(refreshedEditUnit);
		}
	}

	public async delete$($event: unknown): Promise<void> {
		const unit = $event as Unit;
		this.loading.set(true);
		try {
			await this.unitService.delete$(unit.uuid);
			this.units.set(this.units().filter(u => u.uuid !== unit.uuid));
		} catch (error) {
			console.error('Error deleting unit:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$($event: unknown): Promise<void> {
		const unit = $event as Unit;
		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}
		this.loading.set(true);
		try {
			const updatedUnits = await this.unitService.moveUp$(
				unit.uuid,
				this.adminService.selectedCourse$.value.uuid
			);
			this.units.set(updatedUnits);
		} catch (error) {
			console.error('Error moving unit up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$($event: unknown): Promise<void> {
		const unit = $event as Unit;
		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}
		this.loading.set(true);
		try {
			const updatedUnits = await this.unitService.moveDown$(
				unit.uuid,
				this.adminService.selectedCourse$.value.uuid
			);
			this.units.set(updatedUnits);
		} catch (error) {
			console.error('Error moving unit down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private async createNewUnit$($event: any): Promise<void> {
		if (!this.adminService.selectedCourse$.value) {
			throw new Error('No course selected');
		}

		await this.unitService.create$({
			course_uuid: this.adminService.selectedCourse$.value.uuid,
			default_lang_id: 'en',
			unit_text: [
				{
					default_lang_id: 'en',
					name: $event.name,
					image_uri: $event.imageUri,
					description_short: $event.descriptionShort,
					description_html: $event.descriptionHtml,
					created: new Date(),
					updated: new Date(),
				},
			],
		});
	}

	private async updateUnit$($event: any): Promise<void> {
		const currentEditUnit = this.editUnit();
		if (!currentEditUnit) {
			throw new Error('No unit to update');
		}

		const unitTexts: UnitText[] = currentEditUnit.unit_text;

		let unitText: UnitText | undefined = unitTexts.find(text => text.default_lang_id === this.editLanguage());

		if (!unitText) {
			unitText = {
				uuid: v4(),
				unit_uuid: currentEditUnit.uuid,
				default_lang_id: this.editLanguage(),
				name: $event.name,
				image_uri: $event.imageUri,
				description_short: $event.descriptionShort,
				description_html: $event.descriptionHtml,
				created: new Date(),
				updated: new Date(),
			};

			unitTexts.push(unitText);
		} else {
			unitText.name = $event.name;
			unitText.image_uri = $event.imageUri;
			unitText.description_short = $event.descriptionShort;
			unitText.description_html = $event.descriptionHtml;
			unitText.updated = new Date();
		}

		const props = Object.keys(EMPTY_UNIT);
		const payload: Unit = _.pick(currentEditUnit, props) as Unit;

		await this.unitService.update$(currentEditUnit.uuid, {
			...payload,
			unit_text: unitTexts,
		});
	}

	public onEditLanguageChange(code: string): void {
		this.editLanguage.set(code);
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
